import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { discoveryApi, CandidateStatus, DiscoveryRun } from '../api/discoveryApi'

const OPEN = (r: DiscoveryRun) => r.status === 'queued' || r.status === 'running'

/** Recent runs; polls while one is open. */
export function useDiscoveryRuns() {
  return useQuery({
    queryKey: ['discovery-runs'],
    queryFn: discoveryApi.getRuns,
    refetchInterval: (q) => ((q.state.data?.data ?? []).some(OPEN) ? 5000 : false),
  })
}

/** The latest finished run (or a given one) with its topics and ranked candidates. */
export function useDiscoveryRun(id: number | 'latest') {
  return useQuery({
    queryKey: ['discovery-run', id],
    queryFn: () => discoveryApi.getRun(id),
    retry: false,
    staleTime: 60 * 1000,
  })
}

export function useStartDiscovery() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (body: { topics?: number; skipDemand?: boolean }) => discoveryApi.startRun(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['discovery-runs'] })
      toast({ title: 'Discovery run started', description: 'Demand, gap topics, candidates, then the judge. About 15 minutes.' })
    },
    onError: (e: any) => toast({ title: 'Could not start a run', description: e?.response?.data?.message || String(e), variant: 'destructive' }),
  })
}

export function useDecideCandidate() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: CandidateStatus }) => discoveryApi.decide(id, status),
    onSuccess: (r, v) => {
      qc.invalidateQueries({ queryKey: ['discovery-run'] })
      if (v.status === 'approved') {
        toast({
          title: `${r.data.artistName} is now an AMS artist`,
          description: 'Their catalogue is mirrored on the next daily refresh, then ingested and published automatically.',
        })
      }
    },
    onError: (e: any) => toast({ title: 'Could not save the decision', description: e?.response?.data?.message || String(e), variant: 'destructive' }),
  })
}
